//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import { useTheme } from '@mui/material/styles';
import CircularPercentage from '@/components/_common/CircularPercentage';
import { Tooltip } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';

type ProgressCircleProps = {
  progress: number;
  status: string;
  exception: string;
  errorMessage: string;
};

const ProgressCircle = ({ progress, status, exception, errorMessage }: ProgressCircleProps) => {
  const theme = useTheme();
  const color = theme?.palette?.info?.main;
  let value, label, tooltip;

  switch (exception) {
    case 'cannot copy file to itself':
      tooltip = 'Unable to copy file. Source and destination are the same.';
      break;
    case 'An error occurred (404) when calling the HeadObject operation: Not Found':
      tooltip = 'File not found';
      break;
    case 'cannot move file to itself':
      tooltip = 'Unable to move file. Source and destination are the same';
      break;
    default:
      tooltip = errorMessage || '';
      break;
  }

  const getProgress = () => {
    switch (status) {
      case 'failed':
        return (
          <Tooltip title={tooltip}>
            <StyledPolarisIcon icon="error_filled" view="error" />
          </Tooltip>
        );
      case 'complete':
        return (
          <Tooltip title={tooltip}>
            <StyledPolarisIcon icon="complete" view="complete" />
          </Tooltip>
        );
      case 'created':
        return (
          <Tooltip title={tooltip}>
            <StyledPolarisIcon icon="circle" view="created" />
          </Tooltip>
        );
      case 'queued':
        return (
          <Tooltip title={tooltip}>
            <StyledPolarisIcon icon="queued" view="queued" />
          </Tooltip>
        );
      default:
        value = progress * 100; // progress
        label = (progress * 100).toFixed(0) + '%';
        return (
          <Tooltip title={tooltip}>
            <span>
              <CircularPercentage style={{ margin: 0 }} value={value} color={color} size={40} bgColor={color + '3a'}>
                {label}
              </CircularPercentage>
            </span>
          </Tooltip>
        );
    }
  };

  return <Wrapper>{getProgress()}</Wrapper>;
};

export default ProgressCircle;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  margin: ${({ theme }) => theme.spacing(-1, 0)};
`;

const StyledPolarisIcon = styled(PolarisIcon)<{ view: string }>`
  font-size: 1.8rem;
  padding: 0px;
  border-radius: 50%;
  width: 33px;
  height: 33px;

  ${({ theme, view }) => {
    switch (view) {
      case 'queued':
        return `
          color: var(--grey);
          overflow: visible;
        `;
      case 'created':
        return `
         color: var(--grey${theme.palette.mode === 'light' ? '-medium' : ''})
        `;
      case 'complete':
        return `
        color: ${theme.palette.success.main};
        `;
      case 'error':
      default:
        return `
          color: ${theme.palette.error.main};
          width: 33px;
          height: 33px;
        `;
    }
  }}
`;
